/**
 * TanStack Query hooks for Term Sheets
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type { TermSheet } from '../types';
import { dealKeys } from './useDeals';
import { comparisonKeys } from './useAnalysis';
import { dealOverviewNarrativeKeys } from './useDealOverviewNarrative';
import { compareReasoningKeys } from './useCompareReasoning';

// Query keys
export const termSheetKeys = {
  all: ['termSheets'] as const,
  lists: () => [...termSheetKeys.all, 'list'] as const,
  list: (dealId: number) => [...termSheetKeys.lists(), dealId] as const,
  details: () => [...termSheetKeys.all, 'detail'] as const,
  detail: (id: number) => [...termSheetKeys.details(), id] as const,
  extraction: (id: number) => [...termSheetKeys.detail(id), 'extraction'] as const,
  prescreen: (id: number) => [...termSheetKeys.detail(id), 'prescreen'] as const,
};

// Fetch term sheets for a deal. Polls every 3s while any sheet is in a
// non-terminal state so downstream UI (DealDetailPage Analysis tab,
// Documents badges, etc.) flips to the finished view without a manual
// refresh.
const TERMSHEET_TERMINAL_STATUSES = new Set([
  'EXTRACTED',
  'VALIDATED',
  'ANALYZED',
  'CLASSIFIED',
  'ERROR',
  'REJECTED',
]);

export function useTermSheets(dealId: number) {
  return useQuery({
    queryKey: termSheetKeys.list(dealId),
    queryFn: () => api.termSheets.list(dealId),
    enabled: !!dealId,
    refetchInterval: (query) => {
      const sheets = query.state.data?.term_sheets ?? [];
      if (sheets.length === 0) {return false;}
      const anyInFlight = sheets.some(
        (ts: { status?: string }) =>
          !ts.status || !TERMSHEET_TERMINAL_STATUSES.has(ts.status.toUpperCase()),
      );
      return anyInFlight ? 3000 : false;
    },
  });
}

// Fetch a single term sheet
export function useTermSheet(termSheetId: number) {
  return useQuery({
    queryKey: termSheetKeys.detail(termSheetId),
    queryFn: () => api.termSheets.get(termSheetId),
    enabled: !!termSheetId,
  });
}

// Get extraction status with polling
export function useExtractionStatus(termSheetId: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: termSheetKeys.extraction(termSheetId),
    queryFn: () => api.termSheets.getExtractionStatus(termSheetId),
    enabled: options?.enabled !== false && !!termSheetId,
    refetchInterval: (query) => {
      // Poll every 3 seconds while extraction is in progress
      // Handles both UPPERCASE (backend) and lowercase (legacy) statuses
      const status = query.state.data?.status?.toUpperCase();
      const ACTIVE_STATUSES = [
        'UPLOADED', 'PROCESSING', 'EXTRACTING', 'CLASSIFYING',
        'PASS1_COMPLETE', 'PASS2_RUNNING', 'PENDING',
        'VALIDATING', 'ANALYZING',
      ];
      return status && ACTIVE_STATUSES.includes(status) ? 3000 : false;
    },
  });
}

// Haiku prescreen polling — powers the "Name this deal" microprompt's
// auto-fill. Polls every 1s while status is PENDING/RUNNING and stops
// on DONE or ERROR. `enabled` should be gated on a real term-sheet id
// (skip for mock/placeholder ids).
export function usePrescreen(termSheetId: number | null | undefined, options?: { enabled?: boolean }) {
  const id = termSheetId ?? 0;
  return useQuery({
    queryKey: termSheetKeys.prescreen(id),
    queryFn: () => api.termSheets.getPrescreen(id),
    enabled: options?.enabled !== false && !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'PENDING' || status === 'RUNNING' ? 1000 : false;
    },
  });
}

// Upload a term sheet
export function useUploadTermSheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      dealId,
      file,
      bankId,
      otherBankName,
      extractionMethod,
      onUploadProgress,
    }: {
      dealId: number;
      file: File;
      bankId?: number;
      otherBankName?: string;
      extractionMethod?: string;
      onUploadProgress?: (pct: number) => void;
    }) =>
      api.termSheets.upload(dealId, file, {
        bankId,
        otherBankName,
        extractionMethod,
        onUploadProgress,
      }),
    onSuccess: (newTermSheet: TermSheet) => {
      // Invalidate term sheets list for the deal
      queryClient.invalidateQueries({
        queryKey: termSheetKeys.list(newTermSheet.deal_id),
      });
      // Invalidate deal detail to update term_sheet_count
      queryClient.invalidateQueries({
        queryKey: dealKeys.detail(newTermSheet.deal_id),
      });
      // Adding an offer changes the deal's scorable offer-set, so the
      // cross-bank comparison and both deal-level LLM narratives go stale.
      // Invalidate by dealId PREFIX (not the full key) so every profile
      // lens matches — the profile is the last key segment.
      queryClient.invalidateQueries({
        queryKey: [...comparisonKeys.all, newTermSheet.deal_id],
      });
      queryClient.invalidateQueries({
        queryKey: [...compareReasoningKeys.all, newTermSheet.deal_id],
      });
      queryClient.invalidateQueries({
        queryKey: dealOverviewNarrativeKeys.deal(newTermSheet.deal_id),
      });
    },
  });
}

// Reprocess a term sheet
export function useReprocessTermSheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ termSheetId, force = false }: { termSheetId: number; force?: boolean }) =>
      api.termSheets.reprocess(termSheetId, force),
    onSuccess: (_, { termSheetId }) => {
      // Invalidate extraction status to start polling
      queryClient.invalidateQueries({
        queryKey: termSheetKeys.extraction(termSheetId),
      });
      // Invalidate term sheet detail
      queryClient.invalidateQueries({
        queryKey: termSheetKeys.detail(termSheetId),
      });
    },
  });
}

// Delete a term sheet
export function useDeleteTermSheet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (termSheetId: number) => api.termSheets.delete(termSheetId),
    onSuccess: () => {
      // Invalidate all term sheet lists since we don't know the deal_id
      queryClient.invalidateQueries({
        queryKey: termSheetKeys.lists(),
      });
      // Also invalidate deal lists to update term_sheet_count
      queryClient.invalidateQueries({
        queryKey: dealKeys.lists(),
      });
      // Deleting an offer changes the deal's scorable offer-set. The mutation
      // only carries termSheetId (no dealId), so invalidate the comparison and
      // both deal-level narratives broadly — every deal/profile entry refetches.
      queryClient.invalidateQueries({ queryKey: comparisonKeys.all });
      queryClient.invalidateQueries({ queryKey: compareReasoningKeys.all });
      queryClient.invalidateQueries({ queryKey: dealOverviewNarrativeKeys.all });
    },
  });
}
